/**
 * The feature intros: short promo films for one feature of the app, on the live Stippl map, and the
 * paid-social ads built on the same kit (ads/: real phone footage with a dive into the live map). They
 * ship in the tripmovie bundle (the one the render worker serves) and are registered in
 * ../tripmovie/index.tsx, like the social compositions and the city guide.
 */
import { Composition } from "remotion";
import type { BookingIntroProps } from "./booking/BookingIntro";
import { BOOKING_SLOW_FRAMES, BookingIntroSlow } from "./booking/BookingIntroSlow";
import { BOOKING_MEDIA } from "./booking/media";
import { BOOKING_FPS } from "./booking/scene";
import { WORK_OF_ART_DIVE } from "./ads/examples";
import { AD_FPS, AD_H, AD_W, ScreenDive, type ScreenDiveProps } from "./ads/ScreenDive";

const bookingDefaults = (layout: BookingIntroProps["layout"]): BookingIntroProps => ({
	layout,
	noMap: false,
	// the full track at 0.66: the balance of music and effects in the approved slowed render (29 Sep)
	musicVolume: 0.66,
	sfxPack: "sfx-hotel",
	media: BOOKING_MEDIA,
});

export const FeatureIntroCompositions: React.FC = () => (
	<>
		<Composition
			id="feature-intro-booking-16x9"
			component={BookingIntroSlow}
			width={1920}
			height={1080}
			fps={BOOKING_FPS}
			durationInFrames={BOOKING_SLOW_FRAMES}
			defaultProps={bookingDefaults("landscape")}
		/>
		<Composition
			id="feature-intro-booking-9x16"
			component={BookingIntroSlow}
			width={1080}
			height={1920}
			fps={BOOKING_FPS}
			durationInFrames={BOOKING_SLOW_FRAMES}
			defaultProps={bookingDefaults("portrait")}
		/>
		{/*
			the paid-social ads from real phone footage: one composition, each ad is its props; the film is as long as the clip.
			Remotion lays the API's props over these defaults one level deep, so a prop an ad leaves out renders the example ad's: that is why
			validateScreenDive (routes/api/social-reel.ts) requires `captions` and `captionY`. Any optional top-level key added to the
			example must be required by validateScreenDive too.
		*/}
		<Composition
			id="ad-screen-dive-9x16"
			component={ScreenDive}
			width={AD_W}
			height={AD_H}
			fps={AD_FPS}
			durationInFrames={WORK_OF_ART_DIVE.durationInFrames}
			defaultProps={WORK_OF_ART_DIVE}
			calculateMetadata={({ props }: { props: ScreenDiveProps }) => ({ durationInFrames: props.durationInFrames })}
		/>
	</>
);
